import { useStateDecisions } from '@/lib/hooks'
import { RunTimeline } from '@/features/state/components/DecisionTimeline'
import { SkeletonCard } from '@/shared/components/Skeleton'
import { formatCount } from '@/lib/format'
import { PageIntro } from '@/shared/components/PageIntro'
import { StatCell } from '@/shared/components/StatCell'

/**
 * The Runs tab: this workspace's terraform/tofu interactions as a ledger.
 *
 * WHY THIS IS NOT THE AUDIT LOG, and not the Overview rollup either. Overview answers "how often
 * and how fast" from state_apply_runs, which by construction holds only runs that ACQUIRED their
 * lock - an apply refused by policy never gets a row there, so the one case worth explaining was
 * the one case invisible. The audit log does record the refusal, but it is actor-centric control
 * plane and gated to Team+, so a Starter org could never see why its own apply broke. This tab
 * merges both sources and is available on every tier.
 */
export function WorkspaceRunsTab({ orgId, workspace }: { orgId: string; workspace: string }) {
  const timeline = useStateDecisions(orgId, workspace)
  const entries = timeline.data?.entries ?? []
  const blocks = entries.filter((e) => e.kind === 'decision' && e.verdict === 'block').length
  const overrides = entries.filter((e) => e.kind === 'decision' && e.verdict === 'override').length
  const runs = entries.filter((e) => e.kind === 'run').length

  return (
    <>
      <PageIntro pageKey="state-workspace-runs" title="What is this tab?">
        <p style={{ margin: 0 }}>
          Every terraform or tofu interaction with this workspace, plotted on a real time axis: the
          runs that took a lock, and the policy decisions that stopped runs from starting. A refused
          apply never acquires a lock, so it appears here and nowhere else. Click a bar for the
          events inside that window, with the reason each decision gives.{' '}
          <strong>Only refusals and overrides are recorded as decisions</strong> - a policy that
          passes leaves no entry, which is why a quiet timeline is a healthy one.
        </p>
      </PageIntro>

      <div className="stat-strip fu" style={{ marginBottom: 16 }}>
        <StatCell label="runs" value={formatCount(runs)} />
        <StatCell label="refusals" value={formatCount(blocks)} color={blocks > 0 ? 'var(--red)' : undefined} />
        <StatCell label="overrides" value={formatCount(overrides)} color={overrides > 0 ? 'var(--amber)' : undefined} />
      </div>

      <div className="card fu">
        <div className="card-header">
          <div>
            <div className="kicker">// run ledger</div>
            <h2 style={{ fontSize: '0.95rem' }}>Activity over time</h2>
          </div>
        </div>
        <div style={{ padding: '18px 20px' }}>
          {timeline.loading ? <SkeletonCard rows={5} />
            : timeline.error ? <p style={{ color: 'var(--red)', fontSize: '0.82rem', margin: 0 }}>{String(timeline.error)}</p>
            : <RunTimeline entries={entries} />}
        </div>
      </div>
    </>
  )
}

